import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTag,
  faPlus,
  faCheck,
  faTrash,
  faUser,
  faClock,
  faEdit,
  faStickyNote
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { useToast } from 'context/providers/ToastProvider';
import FalconCloseButton from 'components/common/FalconCloseButton';

export const CLIENT_COLOR_PRESETS = [
  {
    id: 'yellow',
    name: 'Amarillo',
    hex: '#fef3c7',
    lightBg: '#fef3c7',
    lightBorder: '#fde68a',
    lightText: '#92400e',
    darkBg: 'rgba(245, 158, 11, 0.15)',
    darkBorder: 'rgba(245, 158, 11, 0.35)',
    darkText: '#fbbf24'
  },
  {
    id: 'blue',
    name: 'Azul',
    hex: '#dbeafe',
    lightBg: '#dbeafe',
    lightBorder: '#bfdbfe',
    lightText: '#1e40af',
    darkBg: 'rgba(59, 130, 246, 0.15)',
    darkBorder: 'rgba(59, 130, 246, 0.35)',
    darkText: '#60a5fa'
  },
  {
    id: 'green',
    name: 'Verde',
    hex: '#d1fae5',
    lightBg: '#d1fae5',
    lightBorder: '#a7f3d0',
    lightText: '#065f46',
    darkBg: 'rgba(16, 185, 129, 0.15)',
    darkBorder: 'rgba(16, 185, 129, 0.35)',
    darkText: '#34d399'
  },
  {
    id: 'orange',
    name: 'Naranja',
    hex: '#ffedd5',
    lightBg: '#ffedd5',
    lightBorder: '#fed7aa',
    lightText: '#9a3412',
    darkBg: 'rgba(249, 115, 22, 0.15)',
    darkBorder: 'rgba(249, 115, 22, 0.35)',
    darkText: '#fb923c'
  },
  {
    id: 'purple',
    name: 'Morado',
    hex: '#f3e8ff',
    lightBg: '#f3e8ff',
    lightBorder: '#e9d5ff',
    lightText: '#6b21a8',
    darkBg: 'rgba(168, 85, 247, 0.15)',
    darkBorder: 'rgba(168, 85, 247, 0.35)',
    darkText: '#c084fc'
  }
];

export const getContrastTextColor = (hexColor) => {
  if (!hexColor || !hexColor.startsWith('#') || hexColor.length < 7) {
    return '#1e293b';
  }
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1e293b' : '#ffffff';
};

export const getClientNoteColorStyle = (colorValue, isDark = false) => {
  const normalized = (colorValue || '').toLowerCase().trim();
  const preset = CLIENT_COLOR_PRESETS.find(
    c => (c.hex || '').toLowerCase() === normalized || c.id === normalized
  );

  if (preset) {
    return isDark ? {
      backgroundColor: preset.darkBg,
      borderColor: preset.darkBorder,
      color: preset.darkText
    } : {
      backgroundColor: preset.lightBg,
      borderColor: preset.lightBorder,
      color: preset.lightText
    };
  }

  if (colorValue && colorValue.startsWith('#')) {
    const textColor = getContrastTextColor(colorValue);
    return {
      backgroundColor: colorValue,
      borderColor: 'rgba(0, 0, 0, 0.12)',
      color: textColor
    };
  }

  const defaultPreset = CLIENT_COLOR_PRESETS[0];
  return isDark ? {
    backgroundColor: defaultPreset.darkBg,
    borderColor: defaultPreset.darkBorder,
    color: defaultPreset.darkText
  } : {
    backgroundColor: defaultPreset.lightBg,
    borderColor: defaultPreset.lightBorder,
    color: defaultPreset.lightText
  };
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) + ' • ' + date.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const ClientNotesModal = ({
  show,
  onClose,
  customerId,
  customerName,
  notes = [],
  isLoadingNotes = false,
  isSavingNote = false,
  initialEditingNote = undefined,
  createNote,
  updateNote,
  deleteNote
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { showSuccessToast, showErrorToast } = useToast();

  const [activeNote, setActiveNote] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedColor, setSelectedColor] = useState(CLIENT_COLOR_PRESETS[0].hex);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const textareaRef = useRef(null);

  const isEditing = Boolean(activeNote && (activeNote.idClienteNotas || activeNote.id));

  useEffect(() => {
    if (show) {
      setDeletingNoteId(null);
      setIsDeleting(false);

      if (initialEditingNote) {
        // Modo edición de nota específica (Support Engine)
        setActiveNote(initialEditingNote);
        setIsAdding(true);
        setNoteText(initialEditingNote.note || '');
        setSelectedColor(initialEditingNote.noteColor || CLIENT_COLOR_PRESETS[0].hex);
        setTimeout(() => textareaRef.current?.focus(), 100);
      } else {
        // Apertura general o Clic en "Nueva Nota": enfocar directamente para escribir
        setActiveNote(null);
        setIsAdding(true);
        setNoteText('');
        setSelectedColor(CLIENT_COLOR_PRESETS[0].hex);
        setTimeout(() => textareaRef.current?.focus(), 100);
      }
    } else {
      setActiveNote(null);
      setIsAdding(false);
      setNoteText('');
      setDeletingNoteId(null);
      setIsDeleting(false);
    }
  }, [show, initialEditingNote]);

  const handleStartCreate = () => {
    setActiveNote(null);
    setNoteText('');
    setSelectedColor(CLIENT_COLOR_PRESETS[0].hex);
    setIsAdding(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleStartEdit = (noteItem) => {
    setActiveNote(noteItem);
    setNoteText(noteItem.note || '');
    setSelectedColor(noteItem.noteColor || CLIENT_COLOR_PRESETS[0].hex);
    setIsAdding(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleCancelEditor = () => {
    setActiveNote(null);
    setIsAdding(false);
    setNoteText('');
    setSelectedColor(CLIENT_COLOR_PRESETS[0].hex);
  };

  const isCustomColor = !CLIENT_COLOR_PRESETS.some(
    p => p.hex.toLowerCase() === (selectedColor || '').toLowerCase()
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = noteText.trim();
    if (!text) {
      showErrorToast('Por favor escribe el contenido de la nota.');
      return;
    }

    try {
      if (isEditing) {
        const noteId = activeNote.idClienteNotas || activeNote.id;
        if (updateNote) {
          await updateNote({
            idClienteNotas: noteId,
            note: text,
            noteColor: selectedColor
          });
          showSuccessToast('Nota de cliente actualizada correctamente');
        }
      } else {
        if (createNote) {
          await createNote({
            note: text,
            noteColor: selectedColor
          });
          showSuccessToast('Nota de cliente agregada correctamente');
        }
      }

      setActiveNote(null);
      setNoteText('');
      setSelectedColor(CLIENT_COLOR_PRESETS[0].hex);
      setIsAdding(false);
    } catch (err) {
      showErrorToast(err.message || (isEditing ? 'Error al actualizar la nota' : 'Error al guardar la nota'));
    }
  };

  const handleDelete = async (noteIdToDelete) => {
    const targetId = noteIdToDelete || activeNote?.idClienteNotas || activeNote?.id;
    if (!targetId) return;

    setIsDeleting(true);
    try {
      if (deleteNote) {
        await deleteNote({ idClienteNotas: targetId });
        showSuccessToast('Nota de cliente eliminada correctamente');
      }

      if (activeNote && (activeNote.idClienteNotas === targetId || activeNote.id === targetId)) {
        setActiveNote(null);
        setIsAdding(false);
        setNoteText('');
      }
    } catch (err) {
      showErrorToast(err.message || 'Error al eliminar la nota');
    } finally {
      setIsDeleting(false);
      setDeletingNoteId(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={!isSavingNote && !isDeleting}
    >
      {/* Header */}
      <Modal.Header
        className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0'
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-3 p-2 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: isDark ? 'rgba(52, 211, 153, 0.2)' : '#dcfce7',
              color: isDark ? '#34d399' : '#059669',
              width: '36px',
              height: '36px'
            }}
          >
            <FontAwesomeIcon icon={faUserTag} style={{ fontSize: '1rem' }} />
          </div>
          <div>
            <h5 className="modal-title fw-bold mb-0" style={{ fontSize: '1.02rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
              Notas del Cliente #{customerId || '—'}
            </h5>
            <p className="mb-0 text-muted" style={{ fontSize: '0.78rem' }}>
              {customerName ? `${customerName} • ` : ''}
              {notes.length} {notes.length === 1 ? 'nota registrada' : 'notas registradas'}
            </p>
          </div>
        </div>

        <FalconCloseButton onClick={onClose} disabled={isSavingNote || isDeleting} />
      </Modal.Header>

      {/* Body */}
      <Modal.Body
        className="px-4 py-3"
        style={{
          backgroundColor: isDark ? '#0b141a' : '#f8fafc'
        }}
      >
        {/* ===================== LISTA DE NOTAS EXISTENTES ===================== */}
        {isLoadingNotes && notes.length === 0 ? (
          <div className="text-center py-4 text-muted" style={{ fontSize: '0.82rem' }}>
            <Spinner size="sm" animation="border" className="me-2 text-primary" />
            Cargando notas del cliente...
          </div>
        ) : notes.length === 0 ? (
          <div
            className="py-4 px-3 text-center rounded-3 border mb-3 d-flex flex-column align-items-center justify-content-center"
            style={{
              backgroundColor: isDark ? '#121e2d' : '#ffffff',
              borderColor: isDark ? '#233142' : '#e2e8f0',
              borderStyle: 'dashed'
            }}
          >
            <FontAwesomeIcon icon={faStickyNote} className="mb-2 text-primary" style={{ fontSize: '1.25rem', opacity: 0.8 }} />
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
              Este cliente aún no tiene notas registradas.
            </span>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2 mb-3 se-custom-scrollbar" style={{ maxHeight: '340px', overflowY: 'auto' }}>
            {notes.map((n) => {
              const noteId = n.idClienteNotas || n.id;
              const noteStyle = getClientNoteColorStyle(n.noteColor, isDark);
              const isConfirming = deletingNoteId === noteId;
              const isSelectedForEdit = activeNote && (activeNote.idClienteNotas === noteId || activeNote.id === noteId);

              return (
                <div
                  key={noteId}
                  className="rounded-3 p-3 transition-all"
                  style={{
                    backgroundColor: noteStyle.backgroundColor,
                    border: isSelectedForEdit
                      ? '2px solid #0284c7'
                      : `1px solid ${noteStyle.borderColor || (isDark ? '#334155' : '#cbd5e1')}`,
                    boxShadow: isSelectedForEdit
                      ? '0 0 0 2px rgba(2, 132, 199, 0.25)'
                      : '0 1px 3px rgba(0,0,0,0.04)'
                  }}
                >
                  {/* Fila superior: Autor, Fecha y Acciones */}
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2 text-truncate" style={{ fontSize: '0.78rem' }}>
                      <span className="d-inline-flex align-items-center gap-1 fw-semibold" style={{ color: noteStyle.color }}>
                        <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.72rem', opacity: 0.8 }} />
                        {n.createdUser || 'Asesor'}
                      </span>
                      <span style={{ opacity: 0.4 }}>•</span>
                      <span className="d-inline-flex align-items-center gap-1" style={{ color: noteStyle.color, opacity: 0.85, fontSize: '0.75rem' }}>
                        <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.7rem' }} />
                        {formatDateTime(n.createdDate)}
                      </span>
                    </div>

                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(n)}
                        className="btn btn-sm btn-link p-1 text-decoration-none shadow-none border-0"
                        style={{ color: noteStyle.color, opacity: 0.85 }}
                        title="Editar esta nota"
                      >
                        <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.82rem' }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingNoteId(isConfirming ? null : noteId)}
                        className="btn btn-sm btn-link p-1 text-decoration-none shadow-none border-0 text-danger"
                        style={{ opacity: 0.85 }}
                        title="Eliminar esta nota"
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.82rem' }} />
                      </button>
                    </div>
                  </div>

                  {/* Confirmación inline de eliminación */}
                  {isConfirming && (
                    <div
                      className="p-2 mb-2 rounded-2 d-flex align-items-center justify-content-between"
                      style={{
                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                        border: '1px solid rgba(239, 68, 68, 0.4)'
                      }}
                    >
                      <span className="text-danger small fw-semibold" style={{ fontSize: '0.76rem' }}>
                        ¿Eliminar esta nota?
                      </span>
                      <div className="d-flex align-items-center gap-1.5">
                        <Button
                          variant="danger"
                          size="sm"
                          className="py-0.5 px-2"
                          style={{ fontSize: '0.72rem' }}
                          onClick={() => handleDelete(noteId)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? <Spinner size="sm" animation="border" /> : 'Sí, eliminar'}
                        </Button>
                        <button
                          type="button"
                          className="btn btn-sm py-0.5 px-2.5 shadow-none fw-semibold"
                          style={{
                            fontSize: '0.72rem',
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            color: isDark ? '#38bdf8' : '#0284c7',
                            border: isDark ? '1px solid #334155' : '1px solid #cbd5e1'
                          }}
                          onClick={() => setDeletingNoteId(null)}
                          disabled={isDeleting}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Texto de la nota */}
                  <div
                    style={{
                      fontSize: '0.86rem',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: noteStyle.color
                    }}
                  >
                    {n.note}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===================== DESPLEGABLE / FORMULARIO INLINE ABAJO ===================== */}
        {!isAdding && notes.length > 0 ? (
          <button
            type="button"
            onClick={handleStartCreate}
            className="btn w-100 py-2 d-flex align-items-center justify-content-center gap-1.5 shadow-none"
            style={{
              fontSize: '0.82rem',
              fontWeight: 600,
              borderRadius: '8px',
              border: isDark ? '1px dashed rgba(56, 189, 248, 0.4)' : '1px dashed #7dd3fc',
              backgroundColor: isDark ? '#121e2d' : '#ffffff',
              color: isDark ? '#38bdf8' : '#0284c7',
              transition: 'all 0.15s ease'
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Agregar nueva nota</span>
          </button>
        ) : (
          <div
            className="p-3 rounded-3 border"
            style={{
              backgroundColor: isDark ? '#121e2d' : '#ffffff',
              borderColor: isDark ? '#233142' : '#e2e8f0'
            }}
          >
            {/* Cabecera del formulario inline */}
            <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold" style={{ fontSize: '0.82rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                  {isEditing ? 'Editar Nota' : 'Nueva Nota'}
                </span>
                {isEditing && (
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '0.68rem' }}>
                    Modificando
                  </span>
                )}
              </div>

              {/* Selector de color */}
              <div className="d-flex align-items-center gap-1.5">
                {CLIENT_COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setSelectedColor(preset.hex)}
                    className="rounded-circle border-0 p-0 position-relative d-flex align-items-center justify-content-center"
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: preset.hex,
                      border: selectedColor.toLowerCase() === preset.hex.toLowerCase() ? '2px solid #0284c7' : '1px solid #cbd5e1',
                      transform: selectedColor.toLowerCase() === preset.hex.toLowerCase() ? 'scale(1.15)' : 'scale(1)',
                      transition: 'all 0.15s ease',
                      cursor: 'pointer'
                    }}
                    title={preset.name}
                  >
                    {selectedColor.toLowerCase() === preset.hex.toLowerCase() && (
                      <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.55rem', color: preset.lightText }} />
                    )}
                  </button>
                ))}

                {/* Paleta arcoíris para color personalizado */}
                <label
                  className="rounded-circle position-relative d-flex align-items-center justify-content-center m-0"
                  style={{
                    width: '20px',
                    height: '20px',
                    background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                    border: isCustomColor ? '2px solid #0284c7' : '1px solid #cbd5e1',
                    transform: isCustomColor ? 'scale(1.15)' : 'scale(1)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  title="Elegir color personalizado"
                >
                  <input
                    type="color"
                    value={selectedColor.startsWith('#') && selectedColor.length === 7 ? selectedColor : '#d1fae5'}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                  />
                  {isCustomColor && (
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.55rem', color: '#ffffff', textShadow: '0 0 2px rgba(0,0,0,0.8)' }} />
                  )}
                </label>
              </div>
            </div>

            {/* Formulario / Textarea */}
            <Form onSubmit={handleSubmit}>
              <Form.Control
                ref={textareaRef}
                as="textarea"
                rows={3}
                placeholder="Escribe una nota para este cliente (acuerdos, preferencias, detalles clave)..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                disabled={isSavingNote || isDeleting}
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                  color: isDark ? '#f9fafd' : '#0f172a',
                  borderColor: isDark ? '#334155' : '#cbd5e1',
                  fontSize: '0.85rem',
                  resize: 'none'
                }}
              />

              {/* Botones de acción del desplegable */}
              <div className="d-flex align-items-center justify-content-between mt-2 pt-1">
                <div>
                  {isEditing && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0 d-flex align-items-center gap-1 shadow-none text-decoration-none"
                      style={{ fontSize: '0.78rem' }}
                      onClick={() => handleDelete(activeNote.idClienteNotas || activeNote.id)}
                      disabled={isSavingNote || isDeleting}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>Eliminar nota</span>
                    </Button>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  {(notes.length > 0 || isEditing) && (
                    <button
                      type="button"
                      onClick={handleCancelEditor}
                      disabled={isSavingNote || isDeleting}
                      className="btn btn-sm px-3 shadow-none fw-semibold"
                      style={{
                        fontSize: '0.8rem',
                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                        color: isDark ? '#38bdf8' : '#0284c7',
                        border: isDark ? '1px solid #334155' : '1px solid #cbd5e1'
                      }}
                    >
                      Cancelar
                    </button>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={isSavingNote || isDeleting || !noteText.trim()}
                    style={{ fontSize: '0.8rem', fontWeight: 600, backgroundColor: '#0284c7', borderColor: '#0284c7' }}
                  >
                    {isSavingNote ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-1" />
                        <span>Guardando...</span>
                      </>
                    ) : (
                      <span>{isEditing ? 'Guardar Cambios' : 'Agregar Nota'}</span>
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer
        className="px-4 py-2.5 border-top d-flex justify-content-end align-items-center"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0'
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="btn btn-sm px-3 shadow-none fw-semibold"
          style={{
            fontSize: '0.82rem',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#38bdf8' : '#0284c7',
            border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
            transition: 'all 0.15s ease'
          }}
        >
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

ClientNotesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  customerName: PropTypes.string,
  notes: PropTypes.array,
  isLoadingNotes: PropTypes.bool,
  isSavingNote: PropTypes.bool,
  initialEditingNote: PropTypes.object,
  createNote: PropTypes.func,
  updateNote: PropTypes.func,
  deleteNote: PropTypes.func
};

export default ClientNotesModal;
